import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Main as Container } from 'Components/Main';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../../store/auth';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getGapAnalysisControl, postGapAnalysisVinculo } from 'services/gap-analysis';
import { options, getSolutionKey, translateDomain, translateFunctionGrouping, translateApplicability, typeLabelKeys } from '../../../../utils/labels_utils';
import { Spinner } from 'Components/common/Spinner';
import { PageHeader, Select, TextField, Button } from 'Components/ui';
import RequirementLabel from '../../../../Components/RequirementLabel';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const BLUE_DEEP = 'var(--color-brand-strong)';
const TEXT = 'var(--vo-ink-2)';
const BORDER_IDLE = 'var(--vo-steel)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';

const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 24px;
  margin-bottom: 16px;
`;
const CardHeader = styled.div`
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${DIVIDER};
`;
const ControlCode = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BORDER_IDLE};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`;
const ControlName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
  letter-spacing: -.01em;
`;
const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 16px;
`;
const MetaItem = styled.div``;
const MetaLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 5px;
`;
const MetaBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${BLUE_DEEP};
  background: var(--color-brand-8);
  padding: 3px 10px;
  border-radius: 10px;
`;
const MetaText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
`;
const MetaWeight = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${INK};
`;
const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 6px;
`;
const SectionText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  margin: 0;
  line-height: 1.65;
`;
const SectionBlock = styled.div`
  margin-bottom: 14px;
`;
const RequirementFrameworkGroup = styled.div`
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;
const RequirementFrameworkName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${TEXT};
  margin: 0 0 6px;
`;
const RequirementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LinkerCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 24px;
  margin-bottom: 16px;
`;
const LinkerTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${DIVIDER};
`;
const LinkerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 720px;
`;

const SelectWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 320px;
`;
const SelectLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;
const PickerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;
const PanelLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 10px;
`;
const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 10px;
`;
const AvailList = styled.div`
  border: 1px solid ${DIVIDER};
  border-radius: 10px;
  overflow: hidden;
  max-height: 240px;
  overflow-y: auto;
`;
const SelList = styled.div`
  border: 1.5px solid ${p => p.$hasItems ? BLUE : BORDER};
  border-radius: 10px;
  overflow: hidden;
  min-height: 100px;
  background: var(--vo-capri-25);
  max-height: 280px;
  overflow-y: auto;
`;
const ARow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--vo-mist);
  cursor: pointer;
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const SRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid ${DIVIDER};
  &:last-child { border-bottom: none; }
`;
const IName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const IDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const RemBtn = styled.button`
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${BORDER_IDLE};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover { color: var(--vo-danger); }
`;
const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
`;
const EmptyTxt = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${BORDER_IDLE};
  font-style: italic;
  margin: 0;
`;

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BORDER_IDLE} strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

export const GapAnalysisControlLinkShow = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const tenant = selectedTenant.slug;
  const { id, control_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isComplianceProgram = location.pathname.startsWith('/compliance-program');
  const backPath = isComplianceProgram ? '/compliance-program' : `/gap-analysis/show/${id}`;

  const [selectedOption, setSelectedOption] = useState('companies');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { isLoading, error, data: gapAnalysis } = useQuery(
    ['getGapAnalysisControl', id, control_id],
    async () => {
      const data = await getGapAnalysisControl(id, control_id);
      return data.status === 200 ? data.data : null;
    }
  );

  const { handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      company_id: selectedTenant._id || '',
      type: 'companies',
      entity: [],
      controls: [],
    }
  });

  useEffect(() => {
    const isAlreadyLinked = item => gapAnalysis?.controlGapAnalysis?.some(
      c => c.entity_id === item._id && c.entity_type === item.type
    );
    if (!selectedOption || selectedOption === 'companies') {
      setItems(selectedTenant
        ? [{ _id: selectedTenant._id, name: selectedTenant.name, description: selectedTenant.description, type: 'companies' }]
          .filter(item => !isAlreadyLinked(item))
        : []);
      return;
    }
    if (!gapAnalysis) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const selectedObj = options.find(o => o.value === selectedOption);
        if (!selectedObj?.fetchFunction) return;
        const response = await selectedObj.fetchFunction(tenant);
        const itemsWithType = response.data
          .map(item => ({ ...item, type: selectedObj.value }))
          .filter(item => !isAlreadyLinked(item));
        setItems(itemsWithType || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedOption, tenant, selectedTenant, gapAnalysis]);

  if (isLoading) return <p>{t('common:labels.loading')}</p>;
  if (error) return <p>{t('gap-analysis:controlsLink.error')}</p>;

  const employeeSize = parseFloat(selectedTenant.size);
  const solutionKey = getSolutionKey(employeeSize);

  const selectedEntities = watch('entity') || [];

  const unselectedEntities = items.filter(i => !selectedEntities.find(e => e._id === i._id));
  const availableEntities = unselectedEntities
    .filter(i => !searchTerm || i.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Why the "available" list is empty: nothing matches the search, every
  // remaining option is already picked, or there was never anything to pick
  // (items already exclude entities linked to this control).
  const availableEmptyKey = (() => {
    if (unselectedEntities.length > 0 && searchTerm) return 'gap-analysis:controlsLink.noSearchResults';
    const hasLinkedOfType = gapAnalysis?.controlGapAnalysis?.some(c => c.entity_type === selectedOption);
    if (items.length > 0 || hasLinkedOfType) return 'gap-analysis:controlsLink.noMoreEntitiesAvailable';
    return 'gap-analysis:controlsLink.noEntitiesAvailable';
  })();

  const handleSelectEntity = (item) => setValue('entity', [...selectedEntities, item]);
  const handleRemoveEntity = (item) => setValue('entity', selectedEntities.filter(e => e._id !== item._id));

  const submitVinculos = handleSubmit(async (data) => {
    try {
      const formattedData = {
        ...data,
        entity: data.entity.map(item => ({ id: item._id, type: item.type })),
        controls: [{
          id: gapAnalysis.control._id,
          frameworksIds: gapAnalysis.frameworks.map(fw => fw._id),
        }],
      };
      const { data: linkResult } = await postGapAnalysisVinculo(id, formattedData);
      navigate(backPath, { state: { linkResult } });
    } catch (err) {
      console.error('Error linking control:', err);
      navigate(backPath, { state: { linkResult: { code: 'GA_LINK_ERROR' } } });
    }
  });

  const control = gapAnalysis.control;
  const frameworks = gapAnalysis.frameworks || [];

  return (
    <Container>
      <PageHeader
        title={t('gap-analysis:controlLink.title')}
        segments={isComplianceProgram
          ? [
              { label: t('common:breadcrumbs.manage') },
              { label: t('common:sidebar.privacyProgram'), route: '/compliance-program' },
              { label: control.scfId },
              { label: t('gap-analysis:controlLink.title') },
            ]
          : [
              { label: t('common:breadcrumbs.analysis') },
              { label: t('gap-analysis:page.title'), route: '/gap-analysis' },
              { label: control.scfId },
              { label: t('gap-analysis:controlLink.title') },
            ]}
      />

      {/* Control info card */}
      <InfoCard>
        <CardHeader>
          <ControlCode>{control.scfId}</ControlCode>
          <ControlName>{control.scfControl}</ControlName>
        </CardHeader>

        <MetaRow>
          <MetaItem>
            <MetaLabel>{t('gap-analysis:controlLink.fields.domain').replace(':', '')}</MetaLabel>
            <MetaBadge>{translateDomain(t, control.scfDomain)}</MetaBadge>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t('gap-analysis:controlLink.fields.functionGroup').replace(':', '')}</MetaLabel>
            <MetaText>{translateFunctionGrouping(t, control.nistCsfFunctionGrouping)}</MetaText>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t('gap-analysis:controlLink.fields.applicability').replace(':', '')}</MetaLabel>
            <MetaText>{translateApplicability(t, control.applicabilityStatus)}</MetaText>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{t('gap-analysis:controlLink.fields.controlWeight').replace(':', '')}</MetaLabel>
            <MetaWeight>{control.controlWeighting}</MetaWeight>
          </MetaItem>
        </MetaRow>

        <SectionBlock>
          <SectionLabel>{t('gap-analysis:controlLink.fields.controlDesc').replace(':', '')}</SectionLabel>
          <SectionText>{control.scfControlDescription}</SectionText>
        </SectionBlock>

        {frameworks.some(fw => control.frameworks?.[fw._id]?.length) && (
          <SectionBlock>
            <SectionLabel>{t('gap-analysis:controlLink.fields.requirements').replace(':', '')}</SectionLabel>
            {frameworks
              .filter(fw => control.frameworks?.[fw._id]?.length)
              .map(fw => (
                <RequirementFrameworkGroup key={fw._id}>
                  <RequirementFrameworkName>{fw.sourceIdentifier}</RequirementFrameworkName>
                  <RequirementList>
                    {(fw.requirements || []).map(req => (
                      <RequirementLabel key={req.code} code={req.code} title={req.title} />
                    ))}
                  </RequirementList>
                </RequirementFrameworkGroup>
              ))}
          </SectionBlock>
        )}

        {control[solutionKey] && (
          <SectionBlock style={{ marginBottom: 0 }}>
            <SectionLabel>{t('gap-analysis:controlLink.fields.methods').replace(':', '')}</SectionLabel>
            <SectionText style={{ whiteSpace: 'pre-line' }}>{control[solutionKey]}</SectionText>
          </SectionBlock>
        )}
      </InfoCard>

      {/* Entity linker card */}
      <LinkerCard>
        <LinkerTitle>{t('gap-analysis:controlLink.linkElementsTitle')}</LinkerTitle>
        <LinkerBody>
          <SelectWrap>
            <SelectLabel>{t('gap-analysis:controlLink.entityTypeLabel')}</SelectLabel>
            <Select
              name="entityType"
              value={selectedOption}
              onChange={value => {
                setSelectedOption(value);
                setValue('entity', []);
                setSearchTerm('');
              }}
              options={options.map(opt => ({ value: opt.value, label: t(opt.labelKey) }))}
            />
          </SelectWrap>

          {loading && <Spinner />}

          <PickerGrid>
            <div>
              <PanelLabel>{t('gap-analysis:controlLink.availableLabel')}</PanelLabel>
              <SearchWrap>
                <TextField
                  fullWidth
                  placeholder={t('gap-analysis:controlLink.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingLeft: '38px' } }}
                />
                <SearchIcon />
              </SearchWrap>
              <AvailList>
                {availableEntities.map(item => (
                  <ARow key={item._id} data-testid="available-entity-row" onClick={() => handleSelectEntity(item)}>
                    <div>
                      <IName>{item.name}</IName>
                      {item.description && <IDesc>{item.description}</IDesc>}
                    </div>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={BORDER_IDLE} strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </ARow>
                ))}
                {availableEntities.length === 0 && !loading && (
                  <EmptyState><EmptyTxt data-testid="available-entities-empty">{t(availableEmptyKey)}</EmptyTxt></EmptyState>
                )}
              </AvailList>
            </div>

            <div>
              <PanelLabel>
                {t('gap-analysis:controlLink.selectedLabel', { count: selectedEntities.length })}
              </PanelLabel>
              <div style={{ height: 50 }} />
              <SelList $hasItems={selectedEntities.length > 0}>
                {selectedEntities.map(item => (
                  <SRow key={item._id}>
                    <div>
                      <IName>{item.name}</IName>
                      <IDesc>{typeLabelKeys[item.type] ? t(typeLabelKeys[item.type]) : item.type}</IDesc>
                    </div>
                    <RemBtn onClick={() => handleRemoveEntity(item)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </RemBtn>
                  </SRow>
                ))}
                {selectedEntities.length === 0 && (
                  <EmptyState><EmptyTxt>{t('gap-analysis:controlLink.noEntitySelected')}</EmptyTxt></EmptyState>
                )}
              </SelList>
            </div>
          </PickerGrid>

          <Button
            variant="primary"
            onClick={submitVinculos}
            startIcon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            }
            sx={{ height: '44px', padding: '0 28px', alignSelf: 'flex-start' }}
          >
            {t('gap-analysis:controlLink.saveButton')}
          </Button>
        </LinkerBody>
      </LinkerCard>
    </Container>
  );
};
